import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Edit,
  User,
  ShieldCheck,
  Mail,
  Phone,
  Calendar,
  Loader2,
  BadgeCheck,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import useUser from "../../../hooks/useUser";
import AdminLayout from "../Layout/AdminLayout";

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3">
      {Icon && (
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon size={14} className="text-slate-500" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-medium text-slate-900 mt-0.5">{value || "—"}</p>
      </div>
    </div>
  );
}

function SectionCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#232F3E] flex items-center justify-center">
            <Icon size={20} className="text-white" />
          </div>
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default function UserViewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { fetchUserById } = useUser();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchUserById(id);
        setUser(data);
      } catch (err) {
        console.error("Failed to load user:", err);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatUserType = (type) => {
    const rawType = (type || "").toUpperCase();
    if (rawType === "POS_USER" || rawType === "SELLER") return "SELLER_USER";
    return rawType || "—";
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#C45500] mx-auto" />
            <p className="mt-3 text-slate-600 font-medium">Loading user data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-gray-500">User not found</p>
          <button onClick={() => navigate("/users")} className="mt-4 px-4 py-2 text-[#C45500] hover:underline">
            Go back to list
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-x-hidden">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 px-4 sm:px-6 py-3 sticky top-0 z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/users")}
                className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all duration-200"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">User Details</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  {user.firstName} {user.lastName}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate(`/users/edit/${id}`)}
              className="px-5 py-2.5 bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] text-[#0F1111] rounded-xl hover:from-[#f0c14b] hover:to-[#e7ae00] flex items-center gap-2 text-sm font-semibold transition-all duration-200 shadow-sm"
            >
              <Edit size={16} />
              Edit User
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-6 py-6 space-y-6">

            {/* Profile Header Card */}
            <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden p-6">
              <div className="flex items-start gap-5">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#f08804] to-[#e88b00] flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                  {user.firstName?.charAt(0)}{user.lastName?.charAt(0) || ""}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-xl font-bold text-gray-900">
                      {user.firstName} {user.lastName}
                    </h2>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      user.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                    }`}>
                      {user.active !== false ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{formatUserType(user.userType)}</p>
                  <div className="flex flex-wrap items-center gap-5">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Mail size={14} className="text-gray-400" />
                      {user.email || "—"}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Phone size={14} className="text-gray-400" />
                      {user.phoneNumber || "—"}
                    </div>
                    {user.dateOfBirth && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Calendar size={14} className="text-gray-400" />
                        {formatDate(user.dateOfBirth)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <SectionCard icon={User} title="Personal Information">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-1 divide-slate-100">
                <InfoItem icon={User} label="First Name" value={user.firstName} />
                <InfoItem icon={User} label="Last Name" value={user.lastName} />
                <InfoItem icon={Mail} label="Email" value={user.email} />
                <InfoItem icon={Phone} label="Phone Number" value={user.phoneNumber} />
                <InfoItem icon={User} label="Gender" value={user.gender} />
                <InfoItem icon={Calendar} label="Date of Birth" value={formatDate(user.dateOfBirth)} />
                <InfoItem icon={User} label="Username" value={user.username} />
                <InfoItem icon={User} label="User Type" value={formatUserType(user.userType)} />
              </div>
            </SectionCard>

            {/* Auth & Access */}
            <SectionCard icon={ShieldCheck} title="Auth & Access">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1">
                <InfoItem icon={ShieldCheck} label="User Type" value={formatUserType(user.userType)} />
                <InfoItem icon={User} label="Username" value={user.username} />
                <InfoItem
                  icon={User}
                  label="Status"
                  value={
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      user.active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}>
                      {user.active !== false ? "Active" : "Inactive"}
                    </span>
                  }
                />
              </div>
            </SectionCard>

          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
